import React, { useState, useEffect } from 'react';
import { X, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { LifecycleTemplateView, EmployeeSelectionView, OnboardingProcessInput } from '../../../../models/employee';
import { startOnboarding, getEmployeeSelections } from '../../../../api/Employee/onboardingProcesses';
import { DateField } from '../../../../components/dateFields';

interface StartOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  templates: LifecycleTemplateView[];
}

const StartOnboardingModal: React.FC<StartOnboardingModalProps> = ({ isOpen, onClose, onSubmit, templates }) => {
  const { t } = useTranslation();
  const currentUserId = useSelector((state: any) => state.user.userId);
  const [employees, setEmployees] = useState<EmployeeSelectionView[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSelectionView | null>(null);
  const [templateId, setTemplateId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [managerId, setManagerId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLoadingEmployees(true);
      getEmployeeSelections()
        .then(result => {
          if (!result.errors || result.errors.length === 0) {
            setEmployees(result.payload || []);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingEmployees(false));

      // Reset form
      setSelectedEmployee(null);
      setTemplateId(templates?.find(t => t.isDefault)?.id?.toString() || templates?.[0]?.id?.toString() || '');
      setStartDate('');
      setManagerId('');
      setError(null);
    }
  }, [isOpen, templates]);

  const handleEmployeeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const employee = employees.find(emp => emp.id === e.target.value);
    setSelectedEmployee(employee || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || !managerId || !startDate) return;

    setSubmitting(true);
    setError(null);

    try {
      const input: OnboardingProcessInput = {
        userId: selectedEmployee.id,
        templateId: templateId ? Number(templateId) : undefined,
        startDate,
        managerId,
      };

      const result = await startOnboarding(input);
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || 'Failed to start onboarding');
      } else {
        onSubmit();
      }
    } catch {
      setError('Failed to start onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Start New Onboarding')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('Create onboarding process for new employee')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
          )}

          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Select Employee')} <span className="text-red-500">*</span>
            </label>
            {loadingEmployees ? (
              <div className="flex items-center text-gray-500 text-sm py-2">
                <Loader className="w-4 h-4 animate-spin mr-2" /> {t('Loading employees...')}
              </div>
            ) : (
              <select
                required
                value={selectedEmployee?.id || ''}
                onChange={handleEmployeeSelect}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{t('Choose an employee...')}</option>
                {employees.filter(emp => emp.id !== currentUserId).map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.fullName} - {emp.title || ''} ({emp.department || ''})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Employee Details (Auto-filled) */}
          {selectedEmployee && (
            <div className="bg-muted border border-border rounded-lg p-4 grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('Position')}</span>
                <div className="text-sm font-medium text-gray-900">{selectedEmployee.title || '-'}</div>
              </div>
              <div>
                <span className="text-xs text-gray-500">{t('Department')}</span>
                <div className="text-sm font-medium text-gray-900">{selectedEmployee.department || '-'}</div>
              </div>
            </div>
          )}

          {/* Onboarding Template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Onboarding Template')}
            </label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('No template (empty process)')}</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.isDefault ? t('(Default)') : ''}
                </option>
              ))}
            </select>
            {templateId && (
              <p className="text-xs text-gray-500 mt-1">
                {templates.find(t => t.id === Number(templateId))?.description}
              </p>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Start Date')} <span className="text-red-500">*</span>
            </label>
            <DateField
              required
              value={startDate}
              onChange={(v) => setStartDate(v)}
            />
          </div>

          {/* Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Reporting Manager')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('Select a manager...')}</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName} - {emp.title || ''}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={!selectedEmployee || !startDate || !managerId || submitting}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {submitting && <Loader className="w-4 h-4 animate-spin mr-2" />}
              {t('Create Onboarding')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartOnboardingModal;
